<template>
  <footer
    class="relative px-4 phone:border-t phone:border-bor-nav phone:bg-elev-panel-bg phone:px-0 phone:py-2"
    data-test="chat-footer"
  >
    <div
      v-if="currentUser?.imLogin !== room.pServedUser && !eventIdForQuoting"
      class="flex min-h-6 w-full min-w-0 items-center justify-center phone:min-h-0 phone:px-2"
    >
      <typing-label
        class="flex w-full min-w-0 justify-center text-fg-base-fg22"
        :p-served="room.pServedUser"
        :is-group="room.isGroup"
      />
    </div>

    <chat-footer-extended-info
      :p-served="pServed"
    />

    <mention-list
      v-if="isMentioning && contactsSizeMoreThanTwo && availableOptions.length > 0"
      v-click-outside="endMention"
      class="mb-1 phone:mb-0"
      :available-options="availableOptions"
      :selected-option-index="selectedOptionIndex"
      @on-option-click="onOptionClick"
    />

    <div
      v-if="isEditing"
      class="
        mb-1 flex items-center gap-2 rounded-12 border border-bor-nav bg-elev-panel-bg pl-3 pr-1.5
        phone:pb-2 phone:rounded-none phone:border-0 phone:border-b phone:border-bor-nav phone:pl-3 phone:pr-1.5 phone:mb-2
      "
      data-test="chat-editing-banner"
    >
      <wui-icon
        name="editM"
        class="shrink-0 text-surf-brand-basedarkblue-def"
      />
      <div
        v-if="editingAttachmentPreview"
        class="size-10 shrink-0 overflow-hidden rounded-8"
        data-test="chat-editing-attachment-preview"
      >
        <file-icon
          class="h-full w-full"
          :mime="editingAttachmentPreview.filemime ?? ''"
          :cookie="editingAttachmentPreview.filecookie"
          :name="editingAttachmentPreview.filename"
          :open-preview-on-click="false"
          :object-fit="isImageFile(editingAttachmentPreview.filename) ? 'cover' : 'contain'"
          :bordered="false"
        />
      </div>
      <div class="min-w-0 grow py-2 phone:py-0">
        <p class="truncate text-label-m-12-18 text-surf-brand-basedarkblue-def">
          {{ $t('chat.editing') }}
        </p>
        <p
          v-if="editingPreviewText"
          class="truncate text-label-r-12-18 text-fg-base-fg10"
        >
          {{ editingPreviewText }}
        </p>
      </div>
      <wui-btn
        prepend-icon="closeRegularMdM"
        variant="neut"
        state="alpha"
        icon
        data-test="chat-editing-cancel"
        @click="onCloseEditClick"
      >
        <span class="sr-only select-none">{{ $t('chat.closeEditing') }}</span>
      </wui-btn>
    </div>

    <div
      v-if="!isEditing && !eventIdForQuoting && (quotedEvent?.item.messageText || quotedEvent?.item.files?.length || quotedEvent?.item.forwarding?.forwardingEventId)"
      class="
        mb-1 rounded-12 border border-bor-nav bg-elev-panel-bg pl-3 pr-1.5
        phone:mb-2 phone:rounded-none phone:border-0 phone:border-b phone:border-bor-nav phone:pl-3 phone:pr-1.5
      "
      data-test="chat-reply-banner"
    >
      <quoted-message
        :event="quotedEvent"
        compact
        is-clickable
        :selected-text="isEditing ? editingMessage?.item.quote?.quotedText ?? '' : draft.get()?.quotedText ?? ''"
        @click="onQuotedMessageClick"
      >
        <template #default="{}">
          <remove-quoted-message
            :p-served="pServed"
            @click.stop
            @remove="onQuoteRemove"
          />
        </template>
      </quoted-message>
    </div>

    <input
      ref="fileInput"
      data-test="file-upload-input"
      class="hidden"
      type="file"
      accept="*/*"
      multiple
      @change="onHiddenFileChange($event)"
    >
    <chat-footer-uploading-file-list
      v-if="!isEditing && files?.length"
      class="hidden phone:flex phone:pb-4"
      :files="files"
      :room-id="props.room.id"
      @delete="deleteEditingFile"
      @delete-all="deleteAllEditingFiles"
      @delete-editing-file-to-upload="deleteEditingFileToUpload"
    />
    <div
      class="flex items-end gap-2 phone:items-center"
      :class="isVoiceRecorderVisible ? 'phone:px-2' : 'phone:px-3'"
    >
      <div
        v-if="!isEditing && !isVoiceRecorderVisible"
        class="relative hidden shrink-0 phone:block"
      >
        <div
          v-if="isMobileAttachMenuOpen"
          v-click-outside="closeMobileAttachMenu"
          class="absolute bottom-full left-0 z-40 overflow-hidden rounded-12 bg-elev-context-menu-bg py-1 shadow-overlay"
          data-test="chat-mobile-attach-menu"
        >
          <wui-list :size="40">
            <wui-list-item
              icon="fileM"
              class="text-nowrap"
              data-test="chat-attach-document"
              @click="onMobileAttachDocument"
            >
              {{ $t('chat.attachDocument') }}
            </wui-list-item>
            <hr class="mx-1 my-1 border-t-bor-nav p-0!">
            <wui-list-item
              icon="calendarPlusM"
              class="text-nowrap"
              data-test="chat-attach-schedule"
              @click="onMobileAttachSchedule"
            >
              {{ $t('chat.scheduleEvent') }}
            </wui-list-item>
          </wui-list>
        </div>
        <wui-btn
          variant="neut"
          state="alpha"
          data-test="chat-mobile-attach"
          prepend-icon="plusAddM"
          icon
          @click="toggleMobileAttachMenu"
        >
          <span class="sr-only select-none">{{ $t('common.attachFile') }}</span>
        </wui-btn>
      </div>

      <div
        class="flex min-w-0 grow flex-col rounded-12 border border-bor-nav bg-elev-panel-bg"
        :class="isVoiceRecorderVisible
          ? 'phone:border-0 phone:bg-transparent phone:p-0'
          : 'phone:flex-row phone:items-end phone:rounded-10 phone:border-0 phone:bg-bg-base-bg3 phone:pl-4 phone:pr-1'"
      >
        <chat-text-format-toolbar
          v-if="isTextFormatToolbarVisible && !isVoiceRecorderVisible"
          class="rounded-t-12 phone:hidden"
          :active-formats="activeTextFormats"
          @prepare="prepareTextFormat"
          @format="applyTextFormat"
          @link="openTextFormatLinkDialog"
        />
        <voice-message-recorder
          v-if="isVoiceRecorderVisible"
          :room="room"
        />
        <div
          v-show="!isVoiceRecorderVisible"
          class="flex min-w-0 grow items-end"
        >
          <content-editable
            id="contenteditable"
            ref="input"
            v-model="messageText"
            :placeholder="$t('chat.messagePlaceholder')"
            :input-mode="composerInputMode"
            data-test="chat-input"
            class="
              max-h-[25vh] min-w-0 grow overflow-y-auto whitespace-pre-wrap wrap-anywhere
              px-4 pt-3 pb-1.5 text-body-r-16-24 text-fg-base-fg3 outline-none will-change-scroll
              phone:px-0 phone:py-2
            "
            :no-h-t-m-l="false"
            @pointerdown="onComposerPointerDown"
            @mousedown="onFocus"
            @keydown="onKeyDown"
            @file-picked="onFilePicked($event)"
            @click="onMessageEditorClick"
            @blur="updateDraftMessage"
            @beforeinput.capture="handleTextFormatEditorBeforeInput"
            @input.capture="onEditorInput"
            @edit-my-previous-message="startEditMyPreviousMessage"
            @cancel-editing-my-previous-message="endEdit"
          />
          <wui-btn
            class="emoji-picker-anchor hidden shrink-0 phone:inline-flex"
            data-test="chat-emoji-mobile"
            :prepend-icon="showEmojiList ? 'keyboardM' : 'faceSmileM'"
            variant="neut"
            state="alpha"
            icon
            @mousedown.prevent
            @click="onMobileEmojiToggle"
          >
            <span class="sr-only select-none">{{ $t('chat.openEmoji') }}</span>
          </wui-btn>
        </div>
        <chat-footer-uploading-file-list
          v-if="!isEditing && files?.length"
          :files="files"
          :room-id="props.room.id"
          class="py-2 phone:hidden"
          @delete="deleteEditingFile"
          @delete-all="deleteAllEditingFiles"
          @delete-editing-file-to-upload="deleteEditingFileToUpload"
        />
        <div
          v-if="!isVoiceRecorderVisible"
          class="flex items-center justify-between px-2 pb-2 phone:hidden"
          @mousedown.prevent
        >
          <div class="flex items-center">
            <template v-if="!isEditing">
              <wui-btn
                v-if="extendedInfoData"
                :class="{ 'bg-primary-tints-150!': showExtendedInfo }"
                prepend-icon="gridView"
                variant="neut"
                state="alpha"
                icon
                @click="showExtendedInfo = !showExtendedInfo"
              />
              <user-feature
                v-else
                component-name="ChatFilesUploadButton"
              >
                <wui-btn
                  variant="neut"
                  state="alpha"
                  data-test="chat-attach"
                  prepend-icon="paperclipM"
                  icon
                  @click="onPickFile"
                >
                  <span class="sr-only select-none">{{ $t('common.attachFile') }}</span>
                </wui-btn>
              </user-feature>
              <wui-btn
                variant="neut"
                state="alpha"
                data-test="chat-schedule"
                prepend-icon="calendarPlusM"
                icon
                :title="$t('chat.scheduleEvent')"
                @click="openScheduleDialog"
              >
                <span class="sr-only select-none">{{ $t('chat.scheduleEvent') }}</span>
              </wui-btn>
              <div
                class="mx-0.5 flex h-6 w-2 shrink-0 items-center justify-center"
                aria-hidden="true"
              >
                <div class="h-4 w-px bg-bor-nav" />
              </div>
            </template>
            <wui-btn
              class="emoji-picker-anchor"
              data-test="chat-emoji"
              prepend-icon="faceSmileM"
              variant="neut"
              state="alpha"
              icon
              @mousedown.prevent
              @click="toggleEmojiList"
            >
              <span class="sr-only select-none">{{ $t('chat.openEmoji') }}</span>
            </wui-btn>
            <wui-btn
              v-if="contactsSizeMoreThanTwo"
              variant="neut"
              state="alpha"
              data-test="chat-mention"
              prepend-icon="atM"
              icon
              @mousedown.prevent
              @click="openMentionFromButton"
            >
              <span class="sr-only select-none">@</span>
            </wui-btn>
            <wui-btn
              :class="{ 'bg-primary-tints-150!': isTextFormatTogglePressed }"
              prepend-icon="txtFormatM"
              variant="neut"
              state="alpha"
              :title="$t('chat.textFormatToggle')"
              icon
              data-test="chat-text-format-toggle"
              @mousedown.prevent
              @click="toggleTextFormatToolbar"
            >
              <span class="sr-only select-none">{{ $t('chat.textFormatToggle') }}</span>
            </wui-btn>
          </div>
          <div class="flex items-center">
            <wui-btn
              v-if="canShowVoiceRecordButton"
              variant="neut"
              state="alpha"
              data-test="chat-record-voice-message"
              prepend-icon="micM"
              icon
              @mousedown.prevent
              @click="onStartVoiceRecord"
            >
              <span class="sr-only select-none">{{ $t('chat.recordVoiceMessage') }}</span>
            </wui-btn>
            <wui-btn
              v-else
              :disabled="isSendDisabled"
              variant="brand"
              state="filled"
              data-test="chat-send-message"
              :prepend-icon="isEditing ? 'checkRegularM' : 'paperPlaneM'"
              icon
              @mousedown.prevent
              @click="send"
            >
              <span class="sr-only select-none">{{ $t('chat.sendMessage') }}</span>
            </wui-btn>
          </div>
        </div>
      </div>

      <div
        v-if="!isVoiceRecorderVisible"
        class="relative hidden shrink-0 phone:block"
      >
        <wui-btn
          v-if="canShowVoiceRecordButton"
          variant="neut"
          state="alpha"
          data-test="chat-record-voice-message-mobile"
          prepend-icon="micM"
          icon
          @mousedown.prevent
          @click="onStartVoiceRecord"
        >
          <span class="sr-only select-none">{{ $t('chat.recordVoiceMessage') }}</span>
        </wui-btn>
        <wui-btn
          v-else
          :disabled="isSendDisabled"
          variant="brand"
          state="filled"
          data-test="chat-send-message-mobile"
          :prepend-icon="isEditing ? 'checkRegularM' : 'paperPlaneM'"
          icon
          @mousedown.prevent
          @click="send"
        >
          <span class="sr-only select-none">{{ $t('chat.sendMessage') }}</span>
        </wui-btn>
      </div>
    </div>

    <emoji-picker
      v-if="showEmojiList"
      class="emoji-picker-popover"
      close-on-click-outside
      @append="appendEmoji"
      @backspace="onEmojiBackspace"
      @enter="send"
      @escape="closeEmojiList"
      @close="closeEmojiList"
      @click="updateDraftMessage"
    />
  </footer>
</template>

<script lang="ts" setup>
import type { UnlistenFn } from '@tauri-apps/api/event'
import {
  ClickOutside as vClickOutside,
  useDialog,
  useRenderNotifier,
  WuiBtn,
  WuiIcon,
  WuiList,
  WuiListItem,
} from '@wui/common-library'
import {
  EditMessagePayload,
  MessageIncomingEvent,
  MessageOutgoingEvent,
  useIM,
  IMFile,
  HistoryMessageItem,
} from '@wui/im'
import { CanceledError } from 'axios'
import throttle from 'lodash.throttle'
import { storeToRefs } from 'pinia'
import {
  onImageUpdate,
  onFilesUpdate,
  startListening,
  onClipboardUpdate,
} from 'tauri-plugin-clipboard-api'
import {
  computed,
  defineAsyncComponent,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onDeactivated,
  onMounted,
  onUnmounted,
  Ref,
  ref,
  toRef,
  unref,
  watch,
  shallowRef,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { ScheduleEditDialog, ScheduleEditErrorDialog, ScheduleEditSuccessDialog } from '@/widgets/room-schedule-dialog'

import { useFilesUpload } from '@/features/chat-files-upload'
import { ChatFooterExtendedInfo, useExtendedInfo } from '@/features/chat-footer'
import { ChatTextFormatToolbar, useChatTextFormat } from '@/features/chat-text-format'
import { useMessageEdit } from '@/features/message-edit'
import { grantMicrophonePermission } from '@/features/mic-permission'
import { RemoveQuotedMessage } from '@/features/remove-quoted-message'
import { VoiceMessageRecorder, useVoiceRecorder } from '@/features/voice-message-record'

import { FileIcon } from '@/entities/chat-event-file'
import { useStoreMediaDevices } from '@/entities/media-devices'
import { chatModel, draftModel, QuotedMessage } from '@/entities/messages'
import { TypingLabel } from '@/entities/typing'
import { useRoom } from '@/entities/use-room'
import { isVoiceMessage, useVoiceMessageStore } from '@/entities/voice-message'

import { ContentEditable } from '@/shared/components/content-editable'
import { UserFeature } from '@/shared/components/user-feature'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { useEmoji } from '@/shared/composables/use-emoji'
import { useMention } from '@/shared/composables/use-mention'
import { MAX_UPLOAD_FILES_COUNT, usePromptServer } from '@/shared/composables/web-dav'
import { parseMention } from '@/shared/services/mention-helper'
import { ChatFile } from '@/shared/types/files'
import { Room } from '@/shared/types/store'
import { placeCaretAtEnd } from '@/shared/utils/contenteditable-selection'
import { convertUploadingFiles, convertUploadingFilesFromString } from '@/shared/utils/convert-uploading-files'
import { createLinksFromText } from '@/shared/utils/create-links-from-text'
import {
  chatFileToImFile,
  enrichChatFilesWithMediaDimensions,
  getFileTypeByName,
  isImageFile,
} from '@/shared/utils/files'
import { fullTrim } from '@/shared/utils/full-trim'
import { tauriMode } from '@/shared/utils/import-meta'
import { sanitizeToText } from '@/shared/utils/sanitize'
import { htmlToMessagePayload, serializeTextEntities } from '@/shared/utils/text-entities'

import MentionList from '../mention-list/mention-list.vue'
import ChatFooterUploadingFileList from '../uploading-file-list/uploading-file-list.vue'

const EmojiPicker = defineAsyncComponent(async () => ((await import('@/features/emoji-picker')).EmojiPicker))

const props = defineProps<{
  room: Room,
  eventIdForQuoting?: number
}>()

const im = useIM()
const room = toRef(props, 'room')
const {
  startEdit,
  messageText,
  editingMessage,
  filesToEdit,
  endEdit,
  isEditing,
  isEditingQuotedMessageRemoved,
  originalQuotedEventId,
  isEditingForwardMessageRemoved,
  uploadingFilesInEditingMessage,
} = useMessageEdit()
const input = ref()
const filesNamesToRemoveInEditingMessage = ref<string[]>([])
const editingQuoteEventId = ref(0)
const isFocusedBeforeAppendEmoji = ref(false)
const inputElement: Ref<HTMLElement> = computed(() => input.value?.element)
const {
  isToolbarVisible: isTextFormatToolbarVisible,
  isTogglePressed: isTextFormatTogglePressed,
  activeFormats: activeTextFormats,
  toggleToolbar: toggleTextFormatToolbar,
  prepareFormat: prepareTextFormat,
  applyFormat: applyTextFormat,
  openLinkDialog: openTextFormatLinkDialog,
  handleEditorInput: handleTextFormatEditorInput,
  handleEditorBeforeInput: handleTextFormatEditorBeforeInput,
  handleEditorKeyDown: handleTextFormatEditorKeyDown,
} = useChatTextFormat(inputElement, () => input.value?.update())
const { showEmojiList, toggleEmojiList, openEmojiList, closeEmojiList } = useEmoji()
const roomApi = useRoom()
const { notifyError } = useRenderNotifier()
const {
  removeDraft,
  findByPServed,
  setDraftFiles,
  setDraftText,
  consumeShouldFocusComposerForReply,
} = draftModel.useDraftMessageStore()
const { currentUser } = useCurrentUser()
const { findById, findRoomMessages } = chatModel.useChatMessageStore()
const { removeFile } = usePromptServer()

const {
  updateMention,
  availableOptions,
  selectedOptionIndex,
  isMentioning,
  startMention,
  endMention,
  handleOptionClick,
  handleKeyDown,
} = useMention(input, room)
const { files, uploadFiles } = useFilesUpload(room)
const { t, tc } = useLocalization()
const { showDialog } = useDialog()

const isMobileAttachMenuOpen = ref(false)
const suppressMobileKeyboard = ref(true)

const composerInputMode = computed(() => (suppressMobileKeyboard.value ? 'none' : 'text'))

const onComposerPointerDown = () => {
  if (showEmojiList.value) closeEmojiList()
  suppressMobileKeyboard.value = false
}

const onMobileEmojiToggle = () => {
  if (showEmojiList.value) {
    closeEmojiList()
    suppressMobileKeyboard.value = false
    nextTick(() => inputElement.value?.focus())
    return
  }

  suppressMobileKeyboard.value = true
  inputElement.value?.blur()
  openEmojiList()
}

const onEmojiBackspace = () => {
  const el = inputElement.value
  if (!el) return

  el.focus()
  const selection = document.getSelection()
  if (!selection) return

  if (!selection.rangeCount || !el.contains(selection.anchorNode)) {
    placeCaretAtEnd(el)
  }

  const range = selection.getRangeAt(0)
  if (!range.collapsed) {
    range.deleteContents()
  } else {
    selection.modify('extend', 'backward', 'character')
    if (!selection.isCollapsed) {
      selection.getRangeAt(0).deleteContents()
    }
  }

  selection.collapseToEnd()
  el.dispatchEvent(new Event('input'))
}

const toggleMobileAttachMenu = () => {
  isMobileAttachMenuOpen.value = !isMobileAttachMenuOpen.value
}

const closeMobileAttachMenu = () => {
  isMobileAttachMenuOpen.value = false
}

const openScheduleDialog = async () => {
  closeMobileAttachMenu()
  const response = await showDialog(ScheduleEditDialog, {
    pServed: pServed.value,
    hasOverlay: true,
  }) as { error: Error } | { pServed: string, participants: string[], uid: string }

  if ('error' in response) {
    showDialog(ScheduleEditErrorDialog, {
      error: response.error,
    })
  } else if ('pServed' in response) {
    showDialog(ScheduleEditSuccessDialog, {
      pServed: response.pServed,
      participantsNumbers: response.participants,
      uid: response.uid,
      isCreating: true,
    })
  }
}

const openMentionFromButton = async () => {
  suppressMobileKeyboard.value = false
  await nextTick()
  inputElement.value?.focus()
  document.execCommand('insertText', false, '@')
  startMention()
  updateMention()
  const event = new Event('input')
  inputElement.value?.dispatchEvent(event)
}

const onMobileAttachDocument = () => {
  closeMobileAttachMenu()
  void onPickFile()
}

const onMobileAttachSchedule = () => {
  void openScheduleDialog()
}

const onHiddenFileChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  void onFilePicked(target.files, target)
}

const tauriFiles: Ref<Array<string>> = ref([])

// let unListenTextUpdate: UnlistenFn
let unListenImageUpdate: UnlistenFn
let unListenFiles: UnlistenFn
let unListenClipboard: () => Promise<void>

const areFilesEqual = (file1: IMFile, file2: IMFile): boolean => {
  return file1.filecookie === file2.filecookie
}

const isMessageEdited = computed(() => {
  if (isEditing.value && editingMessage.value) {
    const markup = htmlToMessagePayload(messageText.value)
    if (markup.messageText !== editingMessage.value.item.messageText) {
      return true
    }
    if (serializeTextEntities(markup.entities) !== serializeTextEntities(editingMessage.value.item.entities)) {
      return true
    }

    if (uploadingFilesInEditingMessage.value.length > 0) {
      return true
    }

    const originalFiles = editingMessage.value.item.files ?? []
    const remainingFiles = filesToEdit.value ?? []

    if (originalFiles.length !== remainingFiles.length) {
      return true
    }

    for (let i = 0; i < originalFiles.length; i++) {
      if (!areFilesEqual(originalFiles[i]!, remainingFiles[i]!)) {
        return true
      }
    }

    const currentQuoteId = editingQuoteEventId.value || 0
    const initialQuoteId = originalQuotedEventId.value || 0
    if (currentQuoteId !== initialQuoteId || isEditingQuotedMessageRemoved.value) {
      return true
    }

    return false
  }
  return true
})

const isSendDisabled = computed(() => {
  const text = fullTrim((messageText.value.trim())).length > 0
  const filesExists = !!(files.value?.length && files.value.find(file => unref(file.isLoaded)))
  const allFilesIsLoaded = files.value.every(file => unref(file.isLoaded) || unref(file.isError))

  if (files.value.length) {
    return !(filesExists && allFilesIsLoaded)
  } else {
    return (!text && !filesToEdit.value?.length) || uploadingFilesInEditingMessage.value.length !== 0 || !isMessageEdited.value
  }
})

const pServed = computed(() => room.value.pServedUser)
const { start: startVoiceRecord, pause: pauseVoiceRecord, restoreDraft: restoreVoiceDraft } = useVoiceRecorder()
const voiceMessageStore = useVoiceMessageStore()
const { isMicrophoneAvailable, isMicrophonePermissionGranted } = storeToRefs(useStoreMediaDevices())
const isVoiceRecorderVisible = computed(() => Boolean(voiceMessageStore.getSession(pServed.value)))
const canShowVoiceRecordButton = computed(() => {
  const hasText = fullTrim(messageText.value.trim()).length > 0
  const hasMicrophone = isMicrophoneAvailable.value || !isMicrophonePermissionGranted.value
  return !isEditing.value
    && !hasText
    && !files.value.length
    && !filesToEdit.value?.length
    && !uploadingFilesInEditingMessage.value.length
    && hasMicrophone
})
const onStartVoiceRecord = async () => {
  try {
    if (!isMicrophonePermissionGranted.value) {
      await grantMicrophonePermission()
    }
  } catch (error: unknown) {
    notifyError(error instanceof Error ? error.message : t('common.unknownError'))
    return
  }

  void startVoiceRecord(room.value)
}
const draft = computed(() => findByPServed(pServed.value))
const editingAttachmentPreview = computed(() => {
  const files = filesToEdit.value ?? editingMessage.value?.item.files ?? []
  if (!files.length) return undefined

  return files.find((file) => isImageFile(file.filename)) ?? files[0]
})

const getEditingAttachmentTypeLabel = (filename: string) => {
  const type = getFileTypeByName(filename)
  if (type === 'imageFile') return tc('common.imageP', 0)
  if (type === 'videoFile' || type === 'unplayableVideoFile') return tc('common.videoP', 0)
  return tc('common.fileP', 0)
}

const editingPreviewText = computed(() => {
  if (!editingMessage.value) return ''

  const text = sanitizeToText(editingMessage.value.item.messageText ?? '').trim()
  if (text) return text

  const files = filesToEdit.value ?? editingMessage.value.item.files ?? []
  if (files.length > 1) return t('mainAside.filesDraft', { count: files.length })
  if (files.length === 1) return getEditingAttachmentTypeLabel(files[0]?.filename ?? '')

  return ''
})
const isQuoting = computed(() => {
  return (draft.value.isPresent && !!draft.value.get()?.quotedEventId) || !!editingMessage.value?.item.quote?.quotedEventId || !!props.eventIdForQuoting
})
const quotedEvent = computed(() => {
  let quotedEventId
  if (editingMessage.value?.item.quote?.quotedEventId) {
    quotedEventId = editingMessage.value?.item.quote?.quotedEventId
  }

  if (draft.value.isPresent && draft.value.get()?.quotedEventId) {
    quotedEventId = draft.value.get()?.quotedEventId
  }

  if (props.eventIdForQuoting) {
    quotedEventId = props.eventIdForQuoting
  }

  return quotedEventId ? findById<MessageIncomingEvent | MessageOutgoingEvent>(quotedEventId) : undefined
})

const isMessageTextIsSameAsDraft = computed(() => {
  if (draft.value.isPresent) {
    return draft.value.get()?.messageText === messageText.value
  }
  return !messageText.value
})

const deleteEditingFile = (filecookie: string) => {
  if (filesToEdit.value) {
    const file = filesToEdit.value.find(f => f.filecookie === filecookie)
    if (file) {
      filesNamesToRemoveInEditingMessage.value.push(file.filecookie)
    }

    filesToEdit.value = filesToEdit.value?.filter((file) => {
      return file.filecookie !== filecookie
    })
  }
}

const deleteEditingFileToUpload = (fileToDelete: ChatFile) => {
  if (uploadingFilesInEditingMessage.value) {
    uploadingFilesInEditingMessage.value = uploadingFilesInEditingMessage.value?.filter((file) => {
      return file.file !== fileToDelete.file
    })
  }
}

const deleteAllEditingFiles = () => {
  filesToEdit.value?.forEach(f => {
    filesNamesToRemoveInEditingMessage.value.push(f.filecookie)
  })

  if (filesToEdit.value) {
    filesToEdit.value = []
  }

  if (uploadingFilesInEditingMessage.value) {
    uploadingFilesInEditingMessage.value = []
  }
}

const initMessageText = (pServedUser: string) => {
  try {
    if (pServedUser) {
      findByPServed(pServedUser).ifPresentOrElse((draft) => {
        messageText.value = draft.messageText ?? ''
      }, () => {
        messageText.value = ''
      })
    }
  } catch (e) {
    console.error(e)
  }
}

onMounted(async () => {
  endEdit()
  initMessageText(room.value.pServedUser)
  void restoreVoiceDraft(pServed.value)
  suppressMobileKeyboard.value = true
  if (tauriMode()) {
    onClipboardUpdate(() => {
      if(tauriFiles.value.length > 0) {
        tauriFiles.value = []
      }
    })

    unListenImageUpdate = await onImageUpdate((image: string) => {
      let imageStr = `base64,${image}`
      tauriFiles.value.push(imageStr)
    })

    unListenFiles = await onFilesUpdate((files: string[]) => {
      tauriFiles.value.push(...files)
    })

    unListenClipboard = await startListening()
  }
})

onBeforeUnmount(() => {
  if (voiceMessageStore.getSession(pServed.value)?.status === 'recording') {
    void pauseVoiceRecord(pServed.value)
  }
})

onUnmounted(() => {
  stopExtendedInfo()
  if (tauriMode()) {
    // unListenTextUpdate()
    unListenImageUpdate()
    unListenFiles()
    unListenClipboard()
  }
})

async function send () {
  const text = fullTrim(messageText.value)
  const loadedFiles = files.value.filter(file => unref(file.isLoaded) && !unref(file.isError))
  closeEmojiList()
  if (editingMessage.value) {
    const message = { ...editingMessage.value.item }

    if (isEditingQuotedMessageRemoved.value && message.quote?.quotedEventId) {
      delete message.quote.quotedEventId
    }

    if (isEditingForwardMessageRemoved.value && message.forwarding?.forwardingEventId) {
      const forwarding = {
        ...message.forwarding,
      }
      delete forwarding.forwardingEventId

      message.forwarding = forwarding
    }

    let filesToUpload: IMFile[] = []
    if (filesToEdit.value && filesToEdit.value.length) {
      filesToUpload = [...filesToEdit.value]
    }
    if (loadedFiles?.length) {
      loadedFiles.forEach((file) => {
        filesToUpload.push(chatFileToImFile(file))
      })
    }

    const payload: EditMessagePayload = {
      targetEventId: editingMessage.value.eventId,
      text: createLinksFromText(text),
      to: room.value.pServedUser,
      files: filesToUpload,
      mentionedUsers: parseMention(text, room.value.subscribers),
    }
    if (message.quote?.quotedEventId) {
      payload.quote = {
        targetEventId: message.quote.quotedEventId,
        text: message.quote.quotedText!,
      }
    }
    if (message.forwarding?.forwardingEventId) {
      payload.forward = {
        forwardingEventId: message.forwarding?.forwardingEventId || 0,
        forwardingChatPServed: message.forwarding?.forwardingChatPServed || '',
        forwardingText: message.forwarding.forwardingText ? createLinksFromText(message.forwarding.forwardingText) : '',
        forwardingAuthorName: message.forwarding.forwardingAuthorName!,
      }
    }
    await roomApi.edit(payload)
    const promises = filesNamesToRemoveInEditingMessage.value.map(removeFile)
    Promise.all(promises).finally(() => filesNamesToRemoveInEditingMessage.value = [])
  } else if (isQuoting.value) {
    const quotedEventId = props.eventIdForQuoting ? props.eventIdForQuoting : quotedEvent.value?.eventId
    roomApi.send(createLinksFromText(text), room.value, loadedFiles, quotedEventId, undefined, draft.value.get()?.quotedText)
      .catch((e: Error) => {
        notifyError(e.message)
      })
  } else {
    roomApi.send(createLinksFromText(text), room.value, loadedFiles)
      .catch((e: Error) => {
        notifyError(e.message)
      })
  }

  removeDraft(pServed.value)

  messageText.value = ''
  endEdit()
}

const fileInput = ref<HTMLInputElement>()

const onPickFile = async () => {
  if (!tauriMode()) {
    fileInput.value?.click()
  } else {
    try {
      const { desktopDir } = await import('@tauri-apps/api/path')
      const { open } = await import('@tauri-apps/plugin-dialog')
      const selectedFiles = await open({
        multiple: true,
        defaultPath: await desktopDir(),
      })
      if (selectedFiles && Array.isArray(selectedFiles)) {
        onFilePicked(selectedFiles)
      } else if (selectedFiles) {
        onFilePicked([selectedFiles])
      }
    } catch (err) {
      console.error(err)
    }
  }
}

const onFilePicked = async (
  inputFiles: FileList | Array<string> | null,
  sourceInput?: HTMLInputElement | null,
) => {
  // Attachments are immutable while editing — text only
  if (isEditing.value) return
  if (!inputFiles?.length) return
  if (tauriMode() && tauriFiles.value.length > 0) {
    inputFiles = tauriFiles.value
    tauriFiles.value = []
  }
  const draftFiles: ChatFile[] = [...files.value]

  if (typeof inputFiles[0] === 'string') {
    const converted = await convertUploadingFilesFromString(inputFiles as Array<string>, room.value.id)
    await enrichChatFilesWithMediaDimensions(converted)
    draftFiles.push(...converted)
  } else {
    const converted = convertUploadingFiles(inputFiles as FileList, room.value.id)
    await enrichChatFilesWithMediaDimensions(converted)
    draftFiles.push(...converted)
  }

  if (draftFiles.length) {
    const totalCountOfFiles = (isEditing.value ? (uploadingFilesInEditingMessage.value.length + (filesToEdit.value?.length ?? 0)) : files.value.length) + inputFiles.length
    if (totalCountOfFiles < MAX_UPLOAD_FILES_COUNT) {
      if (!isEditing.value) {
        setDraftFiles(pServed.value, draftFiles)
      } else {
        uploadingFilesInEditingMessage.value.push(...draftFiles)
      }

      try {
        await uploadFiles(draftFiles, pServed.value)

        if (isEditing.value) {
          filesToEdit.value = [...filesToEdit.value ?? [], ...draftFiles.filter(f => uploadingFilesInEditingMessage.value.some(file => file.file === f.file)).map(chatFileToImFile)]

          uploadingFilesInEditingMessage.value = []
        }
      } catch (e: unknown) {
        if (e instanceof CanceledError) {
          return
        }

        const errorMessage = e instanceof Error ? e.message : t('common.unknownError')
        notifyError(errorMessage)
      }

      const inputToReset = sourceInput ?? fileInput.value
      if (inputToReset) {
        inputToReset.value = ''
        inputToReset.files = null
      }
    } else {
      notifyError(t('common.maxFilesError'))
    }
  }
  placeCaretAtEnd(inputElement.value)
}

const onKeyDown = (e: KeyboardEvent) => {
  if (handleTextFormatEditorKeyDown(e)) return

  const mentionAdded = handleKeyDown(e)
  if (!mentionAdded && e.key === 'Enter') {
    if (e.ctrlKey) {
      document.execCommand('insertLineBreak')
    } else if (e.shiftKey) {
      e.preventDefault()
      document.execCommand('insertLineBreak')
    } else if (!e.shiftKey) {
      e.preventDefault()
      if (isSendDisabled.value) return
      send()
    }
  }
}

const onOptionClick = (index: number) => {
  handleOptionClick(index)
  if (inputElement.value) {
    placeCaretAtEnd(inputElement.value)
  }
}

const onMessageEditorClick = (event: MouseEvent) => {
  const target = event.target
  if (!(target instanceof HTMLElement)) return

  const mentionEl = target.closest('[data-contact-uri]')
  if (!(mentionEl instanceof HTMLElement)) return
  if (!inputElement.value?.contains(mentionEl)) return

  // В инпуте клик по mention не открывает карточку — ставим каретку после чипа
  event.preventDefault()
  const selection = window.getSelection()
  if (!selection) return

  const range = document.createRange()
  range.setStartAfter(mentionEl)
  range.collapse(true)
  selection.removeAllRanges()
  selection.addRange(range)
}

const updateDraftMessage = () => {
  if (!isEditing.value) {
    setDraftText(pServed.value, messageText.value)
  }
}

const keepCaretInView = () => {
  requestAnimationFrame(() => {
    const editor = inputElement.value
    if (!editor) return

    const selection = window.getSelection()
    if (!selection?.rangeCount) return

    const range = selection.getRangeAt(0)
    if (!editor.contains(range.startContainer)) return

    const marker = document.createElement('span')
    marker.textContent = '\u200B'

    const markerRange = range.cloneRange()
    markerRange.collapse(false)
    markerRange.insertNode(marker)

    const markerTop = marker.offsetTop
    const markerBottom = markerTop + marker.offsetHeight
    const viewportTop = editor.scrollTop
    const viewportBottom = viewportTop + editor.clientHeight
    const scrollPadding = 8

    if (markerBottom + scrollPadding > viewportBottom) {
      editor.scrollTop = markerBottom - editor.clientHeight + scrollPadding
    } else if (markerTop - scrollPadding < viewportTop) {
      editor.scrollTop = Math.max(0, markerTop - scrollPadding)
    }

    marker.remove()
    selection.removeAllRanges()
    selection.addRange(range)
  })
}

const onInputThrottled = throttle(() => {
  // Если не проверять драфт, тут срабатывает при onMounted, когда еще не печатаем
  if (!isMessageTextIsSameAsDraft.value) {
    im.textTyping({ to: props.room.pServedUser })
  }
  // Нужно для того, чтобы отсылалась команда в im, если текст сообщения идентичен прошлому драфту
  if (!isEditing.value) {
    setDraftText(pServed.value, messageText.value)
  }
}, 1000)

const persistComposerDraft = (): void => {
  onInputThrottled.cancel()
  if (!isEditing.value) {
    setDraftText(pServed.value, messageText.value)
  }
}

onDeactivated(() => {
  persistComposerDraft()
})

onActivated(async () => {
  endEdit()
  initMessageText(pServed.value)
  void restoreVoiceDraft(pServed.value)
  suppressMobileKeyboard.value = true
  await nextTick()
  placeCaretAtEnd(inputElement.value)
})

onBeforeUnmount(() => {
  persistComposerDraft()
})

const onEditorInput = () => {
  keepCaretInView()
  handleTextFormatEditorInput()
  onInputThrottled()
}

const onFocus = () => {
  isFocusedBeforeAppendEmoji.value = true
}

const appendEmoji = (emoji: string) => {
  inputElement.value.focus()

  const selection = document.getSelection()
  const rangeBefore = selection?.getRangeAt(0)
  if (rangeBefore?.endOffset === 0 && !isFocusedBeforeAppendEmoji.value) {
    placeCaretAtEnd(inputElement.value)
  }
  isFocusedBeforeAppendEmoji.value = false

  const range = selection?.getRangeAt(0)
  const isRangeInsideInputElement = inputElement.value && range && inputElement.value.contains(range.startContainer)

  if (isRangeInsideInputElement) {
    range.deleteContents()
    range.insertNode(document.createTextNode(emoji))
    range.collapse(false)
    selection?.removeAllRanges()
    selection?.addRange(range)
    const event = new Event('input')
    inputElement.value?.dispatchEvent(event)
  }
}

const myMessages: Ref<HistoryMessageItem[] | null> = shallowRef(null)

const startEditMyPreviousMessage = () => {
  if (messageText.value.length || files.value?.length || isQuoting.value || isVoiceRecorderVisible.value) {
    return
  }
  myMessages.value = findRoomMessages(pServed.value).filter((event) => event.item.type === 'OutgoingMessageEvent')
  const lastEditable = [...(myMessages.value ?? [])]
    .reverse()
    .find((event) => !isVoiceMessage((event as HistoryMessageItem<MessageOutgoingEvent>).item))
  if (lastEditable) {
    startEdit(lastEditable as HistoryMessageItem<MessageOutgoingEvent>)
  }
}

const onQuoteRemove = () => {
  if (isEditing.value && editingMessage.value) {
    editingMessage.value.item.quote = {
      quotedEventId: 0,
      quotedText: '',
    }
    editingQuoteEventId.value = 0
  }
}

const route = useRoute()
const router = useRouter()

const onQuotedMessageClick = async () => {
  const id = quotedEvent.value?.eventId
  if (!id) return

  if (route.hash === `#${id}`) {
    await router.replace({ path: route.path, query: route.query, hash: '' })
    await nextTick()
  }

  await router.replace({ path: route.path, query: route.query, hash: `#${id}` })
}

const focusComposerForReply = async () => {
  const hasText = fullTrim(messageText.value.trim()).length > 0
  if (hasText) return

  suppressMobileKeyboard.value = false
  await nextTick()
  const el = inputElement.value
  if (!el) return
  el.focus()
  placeCaretAtEnd(el)
}

const onCloseEditClick = () => {
  filesNamesToRemoveInEditingMessage.value = []

  endEdit()
}

watch(pServed, (nextPServed) => {
  initMessageText(nextPServed)
  void restoreVoiceDraft(nextPServed)
  suppressMobileKeyboard.value = true
  closeMobileAttachMenu()
})

watch(() => editingMessage.value, (newEditingMessage, oldEditingMessage) => {
  if (newEditingMessage && !oldEditingMessage) {
    setTimeout(() => {
      placeCaretAtEnd(input.value?.element)
    }, 100)
  }
})

const { extendedInfoData, initExtendedInfo, stopExtendedInfo, showExtendedInfo } = useExtendedInfo(pServed)
initExtendedInfo(inputElement)

watch(() => messageText.value, (newValue) => {
  if (!room.value.isGroup) {
    return
  }
  updateMention()
  if (newValue === '') {
    if (isMentioning) {
      endMention()
    }
  }
})

let tempDraft: string | undefined
watch(() => isEditing.value, (value: boolean) => {
  if (value) {
    closeMobileAttachMenu()
    editingQuoteEventId.value = originalQuotedEventId.value || 0
    draft.value.ifPresent((draft) => {
      tempDraft = draft.messageText
    })
  } else {
    editingQuoteEventId.value = 0
    if (tempDraft) {
      messageText.value = tempDraft
      tempDraft = undefined

      setTimeout(() => {
        placeCaretAtEnd(input.value?.element)
      })
    }
  }
})

watch(quotedEvent, (v) => {
  if (v && !isEditing.value && consumeShouldFocusComposerForReply()) {
    void focusComposerForReply()
  }

  if (v && isEditing.value && editingMessage.value && v.eventId !== editingMessage.value.item.quote?.quotedEventId) {
    editingMessage.value.item.quote = {
      quotedEventId: v.eventId,
    }

    const quotedText = draft.value.get()?.quotedText
    if (quotedText) {
      editingMessage.value.item.quote.quotedText = quotedText
    }
    editingQuoteEventId.value = v.eventId
  }
})

const contactsSizeMoreThanTwo = computed(() => {
  if (room.value.contacts?.length) {
    return room.value.contacts?.length > 2
  }
  return false
})
</script>

<style scoped>
.emoji-picker-popover {
  position: absolute;
  left: 0rem;
  bottom: calc(100% + 8px);
  z-index: 40;
}

.emoji-picker-anchor {
  anchor-name: --chat-footer-emoji-trigger;
}

@supports (left: anchor(--chat-footer-emoji-trigger right)) {
  .emoji-picker-popover {
    left: anchor(--chat-footer-emoji-trigger left);
    right: auto;
    top: auto;
    bottom: anchor(--chat-footer-emoji-trigger top);
    margin-left: 0px;
    translate: none;
  }
}

@media (max-width: 501px) {
  .emoji-picker-popover {
    position: relative;
    right: auto;
    bottom: auto;
    left: auto;
    top: auto;
    margin-left: 0;
    z-index: 20;
    width: 100%;
    translate: none;
  }
}
</style>
